import { useEffect, useState } from "react";
import { regions, sortOptions, years, type SortValue } from "@/mocks/filters";

export interface FilterValue {
  genre: string;
  region: string;
  year: string;
  sort: SortValue;
  subgenre?: string;
}

interface FilterPanelProps {
  value: FilterValue;
  onChange: (next: FilterValue) => void;
  title?: string;
  hideGenre?: boolean;
  genreOptions?: { label: string; value: string }[];
  layout?: "inline" | "sidebar";
}

const chipBase =
  "px-3 h-8 rounded-full text-[12px] whitespace-nowrap transition-colors cursor-pointer flex items-center";

const ChipGroup = ({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onSelect: (value: string) => void;
}) => (
  <div>
    <p className="text-[11px] font-semibold tracking-wider text-foreground-500 mb-2">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value || option.label}
            type="button"
            onClick={() => onSelect(option.value)}
            className={`${chipBase} ${
              active
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default function FilterPanel({
  value,
  onChange,
  hideGenre = false,
  genreOptions,
  layout = "inline",
}: FilterPanelProps) {
  const genreList = genreOptions || [];
  return (
    <div
      className={
        layout === "sidebar"
          ? "flex flex-col gap-5"
          : "rounded-lg border border-background-200 bg-background-50 p-4 flex flex-col gap-4"
      }
    >
      {!hideGenre && genreList.length > 0 ? (
        <ChipGroup
          label="题材分类"
          options={[{ label: "全部", value: "" }, ...genreList]}
          value={value.genre}
          onSelect={(genre) => onChange({ ...value, genre })}
        />
      ) : null}
      <ChipGroup
        label="地区"
        options={[{ label: "全部", value: "" }, ...regions.map((r) => ({ label: r, value: r }))]}
        value={value.region}
        onSelect={(region) => onChange({ ...value, region })}
      />
      <ChipGroup
        label="年份"
        options={[{ label: "全部", value: "" }, ...years.map((y) => ({ label: `${y}`, value: `${y}` }))]}
        value={value.year}
        onSelect={(year) => onChange({ ...value, year })}
      />
      <ChipGroup
        label="排序"
        options={sortOptions.map((s) => ({ label: s.label, value: s.value }))}
        value={value.sort}
        onSelect={(sort) => onChange({ ...value, sort: sort as SortValue })}
      />
    </div>
  );
}

export const useDraftFilter = (value: FilterValue) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return { draft, setDraft };
};